import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../shared/contexts/AuthContext';
import Logo from '../../../shared/components/ui/Logo';

interface SidebarNavItem {
  id: string;
  label: string;
  icon: string;
  disabled: boolean;
}

interface CaptureSidebarProps {
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  isMobile: boolean;
  orgId: string;
  activeNav: string;
  sidebarNav: readonly SidebarNavItem[];
}

const CaptureSidebar: React.FC<CaptureSidebarProps> = ({
  isCollapsed,
  onToggleCollapse,
  isMobile,
  orgId,
  activeNav,
  sidebarNav,
}) => {
  const navigate = useNavigate();
  const { signOut } = useAuth();

  return (
    <div
      className={`bg-white border-r border-gray-200 transition-all duration-300 flex-shrink-0 relative flex flex-col min-h-0 ${
        isMobile ? (isCollapsed ? 'w-0 overflow-hidden' : 'w-64') : isCollapsed ? 'w-16' : 'w-64'
      } ${isMobile && !isCollapsed ? 'z-50 shadow-lg' : ''}`}
    >
      <div className="absolute top-3 sm:top-4 right-0 transform translate-x-1/2 z-10">
        <button
          onClick={onToggleCollapse}
          className="w-10 h-10 bg-white border border-gray-200 rounded-full shadow flex items-center justify-center text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors"
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {isCollapsed ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 5l7 7-7 7M5 5l7 7-7 7" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
            )}
          </svg>
        </button>
      </div>
      <div className="p-3 sm:p-4 flex flex-col h-full min-h-0">
        <div className="flex items-center justify-between mb-4 flex-shrink-0">
          <button
            onClick={() => {
              onToggleCollapse();
              navigate(`/o/${orgId}`);
            }}
            className="flex items-center space-x-2 hover:opacity-80 transition-opacity duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-md p-1"
            title="Go to Homepage"
            aria-label="Go to Homepage"
          >
            <Logo size="md" variant={isCollapsed ? 'mark' : 'full'} />
          </button>
        </div>

        <nav className="space-y-1 flex-1 min-h-0 overflow-y-auto">
          {sidebarNav.map((item) => {
            const isActive = item.id === activeNav;
            return (
              <button
                key={item.id}
                disabled={item.disabled}
                onClick={() => {
                  if (item.disabled) return;
                  if (item.id === 'dashboard') {
                    navigate(`/o/${orgId}`);
                    return;
                  }
                  if (item.id === 'explorer') {
                    navigate(`/o/${orgId}/capture`);
                    if (isMobile) onToggleCollapse();
                    return;
                  }
                  if (item.id === 'match-assets') {
                    navigate(`/o/${orgId}/capture/match-assets`);
                    if (isMobile) onToggleCollapse();
                    return;
                  }
                  if (item.id === 'social-media-uploads') {
                    navigate(`/o/${orgId}/capture/social-media-uploads`);
                    if (isMobile) onToggleCollapse();
                    return;
                  }
                  if (item.id === 'social-media-integrations') {
                    navigate(`/o/${orgId}/capture/social-media-settings`);
                    if (isMobile) onToggleCollapse();
                    return;
                  }
                  if (item.id === 'storage-usage') {
                    navigate(`/o/${orgId}/capture/storage-usage`);
                    if (isMobile) onToggleCollapse();
                    return;
                  }
                }}
                className={`w-full flex items-center rounded-lg text-left transition-colors min-h-44 ${
                  isCollapsed ? 'justify-center px-2 py-1.5' : 'space-x-2 px-3 py-2'
                } ${
                  item.disabled
                    ? 'text-gray-400 cursor-not-allowed opacity-50'
                    : isActive
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                <span className="text-base">{item.icon}</span>
                {!isCollapsed && (
                  <span className="text-sm font-medium">{item.label}</span>
                )}
              </button>
            );
          })}
        </nav>

        {!isCollapsed && (
          <div className="mt-auto pt-3 border-t border-gray-200">
            <button
              onClick={async () => {
                await signOut();
                navigate('/');
              }}
              className="w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              <span className="text-sm font-medium">Logout</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CaptureSidebar;

